import React from "react";

const Invoice = ({ order }) => {

  return (
    <div className="bg-white p-4 sm:p-8 font-[Inter] print:p-0">
      <style>
        {`
          @media print {
            @page {
              margin: 1cm;
              size: auto;
            }
            body {
              -webkit-print-color-adjust: exact;
            }
          }
        `}
      </style>
      <div className="max-w-4xl mx-auto border border-gray-100 shadow-sm p-6 sm:p-10 bg-white print:border-0 print:shadow-none print:p-0 flex flex-col min-h-[calc(100vh-6rem)] print:min-h-[26cm]">
        <div className="flex-grow">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 print:mb-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center overflow-hidden border border-gray-100 shadow-sm">
                  <img 
                    src="/logo.png" 
                    alt="Vihaan Herbals" 
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <span className="text-2xl font-black text-gray-900 tracking-tight">
                  Vihaan Herbals
                </span>
              </div>

              <div className="text-sm text-gray-500 font-medium leading-relaxed">
                Mangaldeep<br />
                Opp Anand Flats, Ahmedabad, Gujarat<br />
                helps.vihaanherbals@gmail.com<br />
              </div>
            </div>

            <div className="text-right">
              <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tighter mb-1">
                Invoice
              </h1>
              <div className="text-sm font-bold text-emerald-600 uppercase tracking-widest">
                #ORD-{order.id}
              </div>

              <div className="mt-4 print:mt-2">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Date Issued
                </p>
                <p className="text-sm font-bold text-gray-900">
                  {new Date(order.created_at).toLocaleDateString("en-US", {
                    month: "long",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </p>
              </div>

              {order.payment_reference && (
                <div className="mt-4 print:mt-2">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Reference ID
                  </p>
                  <p className="text-sm font-bold text-gray-900 font-mono">
                    {order.payment_reference}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="grid grid-cols-2 gap-8 mb-8 print:mb-6">
            <div>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 border-b pb-1">
                Billed To
              </h3>
              <p className="text-sm font-black text-gray-900 mb-0.5">
                {order.user.name}
              </p>
              <p className="text-sm text-gray-600 font-medium leading-relaxed">
                {order.user.email}<br />
                {order.user.phone}
              </p>
            </div>

            <div>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 border-b pb-1">
                Ship To
              </h3>
              <p className="text-sm text-gray-600 font-medium leading-relaxed">
                {order.user.address}
              </p>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8 print:mb-6">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b-2 border-gray-900">
                  <th className="py-2">Product Description</th>
                  <th className="py-2 text-center">Qty</th>
                  <th className="py-2 text-right">Unit Price</th>
                  <th className="py-2 text-right">Total</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {order.order_items.map((item, index) => (
                  <tr key={index}>
                    <td className="py-3">
                      <p className="text-sm font-black text-gray-900 uppercase tracking-tight">
                        {item.product.name}
                      </p>
                      <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mt-0.5">
                        {item.product.category}
                      </p>
                    </td>
                    <td className="py-3 text-center text-sm font-bold text-gray-600">
                      {item.quantity}
                    </td>
                    <td className="py-3 text-right text-sm font-bold text-gray-600">
                      {item.product.formatted_price || `₹${item.price}`}
                    </td>
                    <td className="py-3 text-right text-sm font-black text-gray-900">
                      {item.formatted_total_price || `₹${item.total_price}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end pt-4 border-t-2 border-gray-900 print:pt-2">
            <div className="w-64 space-y-2 print:space-y-1">
              <div className="flex justify-between text-xs font-bold text-gray-500 uppercase tracking-widest">
                <span>Subtotal</span>
                <span>₹{(Number(order.items_total) || (Number(order.total_amount || 0) - Number(order.delivery_charge !== undefined ? order.delivery_charge : 0))).toFixed(2)}</span>
              </div>

              <div
                className={`flex justify-between text-xs font-bold uppercase tracking-widest ${
                  (order.delivery_charge || 0) > 0 ? "text-gray-500" : "text-emerald-600"
                }`}
              >
                <span>Shipping</span>
                <span>{ Number(order.delivery_charge) > 0 ? `₹${Number(order.delivery_charge).toFixed(2)}` : 'FREE' }</span>
              </div>

              <div className="flex justify-between pt-2 border-t border-gray-100 text-xl font-black text-gray-900 uppercase tracking-tighter print:pt-1">
                <span>Total</span>
                <span>{order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-100 text-center print:mt-auto print:mb-4">
          <p className="text-sm font-black text-gray-900 uppercase tracking-widest mb-1 italic">
            Thank you for choosing Vihaan Herbals
          </p>
          <p className="text-[10px] text-gray-400 font-medium">
            This is a computer generated invoice and does not require a physical signature.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Invoice;